

// cache
import Cache from './Cache';

// types


// utils
import { isComplexObject, isFiniteAndPositiveInteger, isFunction, isValueObjectOrArray } from './utils';

/**
 * @private
 *
 * @function customReplacer
 *
 * @description
 * custom replacer for the stringify function
 *
 * @param {string} key key in json object
 * @param {*} value value in json object
 * @returns {*} if function then toString of it, else the value itself
 */
export var customReplacer = function customReplacer(key, value) {
  return isFunction(value) ? '' + value : value;
};

/**
 * @private
 *
 * @function decycle
 *
 * @description
 * ES2015-ified version of cycle.decyle
 *
 * @param {*} object object to stringify
 * @param {Options} options the options passed to the moize method
 * @returns {string} stringified value of object
 */
export var decycle = function decycle(object, options) {
  var cache = new Cache(options);

  /**
   * @private
   *
   * @function coalesceCircularReferences
   *
   * @description
   * recursive method to replace any circular references with a placeholder
   *
   * @param {*} value value in object to decycle
   * @param {string} path path to reference
   * @returns {*} clean value
   */
  var coalesceCircularReferences = function coalesceCircularReferences(value, path) {
    if (!isValueObjectOrArray(value)) {
      return value;
    }

    if (cache.has(value)) {
      return {
        $ref: cache.get(value)
      };
    }

    cache.add(value, path);

    if (Array.isArray(value)) {
      return value.map(function (item, itemIndex) {
        return coalesceCircularReferences(item, path + '[' + itemIndex + ']');
      });
    }

    return Object.keys(value).reduce(function (object, name) {
      object[name] = coalesceCircularReferences(value[name], path + '[' + JSON.stringify(name) + ']');

      return object;
    }, {});
  };

  return coalesceCircularReferences(object, '$');
};

/**
 * @private
 *
 * @function stringify
 *
 * @description
 * stringify with a custom replacer if circular, else use standard JSON.stringify
 *
 * @param {*} value value to stringify
 * @param {function} [replacer] replacer to used in stringification
 * @param {Options} options the options passed to the moize method
 * @returns {string} the stringified version of value
 */
export var stringify = function stringify(value, replacer, options) {
  try {
    return JSON.stringify(value, replacer);
  } catch (exception) {
    return JSON.stringify(decycle(value, options), replacer);
  }
};

/**
 * @private
 *
 * @function getStringifiedArgument
 *
 * @description
 * get the stringified version of the argument passed
 *
 * @param {*} arg argument to stringify
 * @param {function} [replacer] replacer to used in stringification
 * @param {Options} options the options passed to the moize method
 * @returns {string} the stringified argument
 */
export var getStringifiedArgument = function getStringifiedArgument(arg, replacer, options) {
  return isComplexObject(arg) || isFunction(arg) ? stringify(arg, replacer, options) : arg;
};

/**
 * @private
 *
 * @function createArgumentSerializer
 *
 * @description
 * create the internal argument serializer based on the options passed
 *
 * @param {Object} options the options passed
 * @param {boolean} options.serializeFunctions should functions be included in the serialization
 * @param {number} options.maxArgs the cap on the number of arguments used in serialization
 * @returns {function(...Array<*>): string} argument serialization method
 */
export var createArgumentSerializer = function createArgumentSerializer(options) {
  var replacer = options.serializeFunctions ? customReplacer : null;
  var hasMaxArgs = isFiniteAndPositiveInteger(options.maxArgs);

  return function (args) {
    var length = hasMaxArgs ? options.maxArgs : args.length;

    var index = -1,
        key = '|',
        value = void 0;

    while (++index < length) {
      value = getStringifiedArgument(args[index], replacer, options);

      if (value) {
        key += value + '|';
      }
    }

    return key;
  };
};

/**
 * @private
 *
 * @function getSerializerFunction
 *
 * @description
 * based on the options passed, either use the serializer passed or generate the internal one
 *
 * @param {Options} options the options passed to the moized function
 * @returns {function} the function to use in serializing the arguments
 */
export var getSerializerFunction = function getSerializerFunction(options) {
  return isFunction(options.serializer) ? options.serializer : createArgumentSerializer(options);
};


//////////////////
// WEBPACK FOOTER
// ./~/moize/es/serialize.js
// module id = 2402
// module chunks = 0